import React from 'react'

import AreYouSureModal from '../../components/molecules/ChatModals/AreYouSureModal'
import AreYouSureModal2 from '../../components/molecules/ChatModals/AreYouSureModal2'

type AreYouSureContextType = {
  setAction: (action: null | (() => void)) => void
  setReason: React.Dispatch<React.SetStateAction<React.ReactNode>>
  setSubtext: React.Dispatch<React.SetStateAction<React.ReactNode>>
  setTitle: React.Dispatch<React.SetStateAction<string>>
  setV2: React.Dispatch<React.SetStateAction<boolean>>
}

// Sets the type of the value that this provider gives
const AreYouSureContext = React.createContext<AreYouSureContextType>(null)

interface AreYouSureProviderProps {
  children: React.ReactNode
}

const AreYouSureProvider: React.FC<AreYouSureProviderProps> = ({
  children
}) => {
  const [action, _setAction] = React.useState<null | { action: () => void }>(
    null
  )
  const [title, setTitle] = React.useState<string>(undefined)
  const [reason, setReason] = React.useState<React.ReactNode>(null)
  const [subtext, setSubtext] = React.useState<React.ReactNode>(undefined)
  const [v2, setV2] = React.useState(false)
  const open = React.useMemo(() => Boolean(action), [action])

  const setAction: AreYouSureContextType['setAction'] = React.useCallback(
    action => {
      if (action) {
        _setAction({ action })
      } else {
        _setAction(null)
        setReason(null)
        setSubtext(undefined)
        setTitle(undefined)
        setV2(false)
      }
    },
    []
  )

  const triggerAction = React.useCallback(() => {
    if (action?.action) {
      action.action()
    }
    setAction(null)
  }, [action, setAction])

  const handleCloseModal = React.useCallback(() => {
    setAction(null)
  }, [setAction])

  return (
    <>
      <AreYouSureContext.Provider
        value={{ setAction, setReason, setSubtext, setTitle, setV2 }}
      >
        {children}
      </AreYouSureContext.Provider>

      {open &&
        (v2 ? (
          <AreYouSureModal2
            onClose={handleCloseModal}
            onAccept={triggerAction}
            reason={reason}
            subtext={subtext}
            title={title}
          />
        ) : (
          <AreYouSureModal
            open
            onClose={handleCloseModal}
            onAccept={triggerAction}
            reason={reason}
            subtext={subtext}
            title={title}
          />
        ))}
    </>
  )
}

export default AreYouSureProvider

export const useAreYouSure = () => React.useContext(AreYouSureContext)
